import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import MonacoEditor from '@monaco-editor/react';
import * as Y from 'yjs';
import { MonacoBinding } from 'y-monaco';
import * as monaco from 'monaco-editor';
import type { editor } from 'monaco-editor';

const REMOTE_STYLE_ELEMENT_ID = 'yjs-remote-selection-styles';
const remoteClientColorMap: Map<number, string> = new Map();
const trackedClientRefs: WeakMap<object, Set<number>> = new WeakMap();

const ensureStyleElement = (): HTMLStyleElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  let styleEl = document.getElementById(REMOTE_STYLE_ELEMENT_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = REMOTE_STYLE_ELEMENT_ID;
    document.head.appendChild(styleEl);
  }
  return styleEl;
};

const normalizeHexColor = (color: string): string => {
  if (!color) {
    return '#3b82f6';
  }
  let normalized = color.trim();
  if (!normalized.startsWith('#')) {
    normalized = `#${normalized}`;
  }

  if (normalized.length === 4) {
    normalized = `#${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}${normalized[3]}${normalized[3]}`;
  }

  if (normalized.length < 7) {
    normalized = `${normalized}${'0'.repeat(7 - normalized.length)}`;
  }

  return normalized.slice(0, 7);
};

const hexToRgba = (hexColor: string, alpha = 0.2): string => {
  const hex = normalizeHexColor(hexColor).replace('#', '');
  const bigint = Number.parseInt(hex, 16);
  if (Number.isNaN(bigint)) {
    return `rgba(59, 130, 246, ${alpha})`;
  }
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const updateRemoteSelectionStyleSheet = () => {
  const styleEl = ensureStyleElement();
  if (!styleEl) return;
  const rules = Array.from(remoteClientColorMap.entries()).map(([clientId, color]) => {
    const normalized = normalizeHexColor(color);
    const background = hexToRgba(normalized, 0.22);
    return `
.yRemoteSelection-${clientId} {
  background-color: ${background};
}
.yRemoteSelectionHead-${clientId} {
  border-left-color: ${normalized} !important;
  border-right-color: ${normalized} !important;
}
.yRemoteSelectionHead-${clientId}::after {
  background-color: ${normalized} !important;
}
    `;
  }).join('\n');

  styleEl.textContent = rules;
};

interface EditorProps {
  code: string;
  language: string;
  theme: string;
  onChange: (value: string | undefined) => void;
  onSave?: () => void;
  ydoc?: Y.Doc;
  awareness?: any;
  collaborative?: boolean;
  enableIntelliSense?: boolean;
}

const Editor = forwardRef<any, EditorProps>(({ 
  code, 
  language, 
  theme, 
  onChange, 
  onSave,
  ydoc, 
  awareness, 
  collaborative,
  enableIntelliSense = true
}, ref) => {
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const bindingRef = useRef<any>(null);
  const syncRemoteSelectionStyles = React.useCallback(() => {
    if (!collaborative || !awareness || typeof awareness.getStates !== 'function' || !ydoc) {
      return;
    }

    const states: Map<number, any> = awareness.getStates();
    const existingTracked = trackedClientRefs.get(awareness) ?? new Set<number>();
    const nextTracked = new Set<number>();
    let changed = false;

    states.forEach((state, clientId) => {
      if (clientId === ydoc.clientID) {
        return;
      }
      const colorSource = state?.color ?? state?.user?.color;
      if (!colorSource) {
        return;
      }
      const normalizedColor = normalizeHexColor(colorSource);
      nextTracked.add(clientId);
      if (remoteClientColorMap.get(clientId) !== normalizedColor) {
        remoteClientColorMap.set(clientId, normalizedColor);
        changed = true;
      }
    });

    existingTracked.forEach(clientId => {
      if (!nextTracked.has(clientId)) {
        if (remoteClientColorMap.delete(clientId)) {
          changed = true;
        }
      }
    });

    trackedClientRefs.set(awareness, nextTracked);

    if (changed || existingTracked.size !== nextTracked.size) {
      updateRemoteSelectionStyleSheet();
    }
  }, [awareness, collaborative, ydoc]);
  const setupBinding = React.useCallback(() => {
    if (!collaborative || !ydoc || !awareness || !editorRef.current) {
      if (!awareness && collaborative) {
        console.debug('[Editor] Awareness henüz hazır değil, binding beklemede');
      }
      return;
    }

    console.log('[Editor] setupBinding called, creating MonacoBinding...');
    
    const yText = ydoc.getText('monaco');
   
    
    // Add a test listener directly to Y.Doc to verify ANY updates
    const testUpdateHandler = (update: Uint8Array, origin: any) => {
      console.log('[Editor] Y.Doc updateV2 event:', {
        updateSize: update.length,
        origin: origin === null ? 'null (local Monaco edit)' : origin === bindingRef.current ? 'MonacoBinding' : origin?.constructor?.name || 'unknown',
        yTextContent: yText.toString().substring(0, 100)
      });
    };
    ydoc.on('updateV2', testUpdateHandler);
   
    // Observer for Y.Text changes
    const yTextObserver = (event: any) => {
      console.log('[Editor] Y.Text changed:', {
        deltaLength: event.delta?.length || 0,
        currentContent: yText.toString().substring(0, 100),
        transaction: event.transaction ? 'yes' : 'no'
      });
    };
    yText.observe(yTextObserver);
    
    if (bindingRef.current) {
      console.log('  🗑️  Destroying existing binding');
      bindingRef.current.destroy();
    }
    
    bindingRef.current = new MonacoBinding(
      yText,
      editorRef.current.getModel()!,
      new Set([editorRef.current]),
      awareness
    );
    
    console.log('[Editor] ✅ MonacoBinding created successfully');
    
    // Cleanup function
    return () => {
      console.log('[Editor] Cleaning up MonacoBinding and observers');
      ydoc.off('updateV2', testUpdateHandler);
      yText.unobserve(yTextObserver);
    };
  }, [awareness, collaborative, ydoc]);

  useEffect(() => {
    return () => {
      if (bindingRef.current) {
        bindingRef.current.destroy();
        bindingRef.current = null;
      }
    };
  }, []);

  // JavaScript/TypeScript IntelliSense setup
  const setupJavaScriptIntelliSense = () => {
    if (!monacoRef.current) return;

    // JavaScript ayarları
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      allowNonTsExtensions: true,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: monaco.languages.typescript.ModuleKind.CommonJS,
      noEmit: true,
      esModuleInterop: true,
      jsx: monaco.languages.typescript.JsxEmit.React,
      reactNamespace: 'React',
      allowJs: true,
      typeRoots: ['node_modules/@types'],
      lib: ['ES2020', 'DOM', 'DOM.Iterable']
    });

    // TypeScript ayarları
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      allowNonTsExtensions: true,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      noEmit: true,
      esModuleInterop: true,
      jsx: monaco.languages.typescript.JsxEmit.React,
      reactNamespace: 'React',
      allowJs: true,
      strict: false,
      typeRoots: ['node_modules/@types'],
      lib: ['ES2020', 'DOM', 'DOM.Iterable']
    });

    // Diagnostics ayarları
    monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
      diagnosticCodesToIgnore: [1108, 1375, 1378]
    });

    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
      diagnosticCodesToIgnore: [1108, 1375, 1378]
    });
  };

  // Custom completion providers
  const registerCustomCompletions = () => {
    if (!monacoRef.current) return;

    // JavaScript completions
    monaco.languages.registerCompletionItemProvider('javascript', {
      provideCompletionItems: (model, position) => {
        const suggestions: monaco.languages.CompletionItem[] = [
          {
            label: 'console.log',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'console.log(${1:value});',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Log a value to the console',
            range: {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: position.column,
              endColumn: position.column
            }
          },
          {
            label: 'function',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'function ${1:name}(${2:params}) {\n\t${3:// body}\n}',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Create a function',
            range: {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: position.column,
              endColumn: position.column
            }
          }
        ];

        return { suggestions };
      }
    });

    // TypeScript completions
    monaco.languages.registerCompletionItemProvider('typescript', {
      provideCompletionItems: (model, position) => {
        const suggestions: monaco.languages.CompletionItem[] = [
          {
            label: 'interface',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'interface ${1:Name} {\n\t${2:property}: ${3:type};\n}',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Create an interface',
            range: {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: position.column,
              endColumn: position.column
            }
          }
        ];

        return { suggestions };
      }
    });
  };

  useEffect(() => {
    return () => {
      if (bindingRef.current) {
        bindingRef.current.destroy();
        bindingRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!collaborative || !awareness || typeof awareness.on !== 'function') {
      return;
    }

    const logAwarenessStates = () => {
      try {
        if (typeof awareness.getStates !== 'function') {
          console.warn('[Editor] awareness.getStates yok, snapshot alınamadı');
          return;
        }
        const entries = Array.from(awareness.getStates().entries()) as Array<[number, any]>;
      } catch (error) {
        console.warn('[Editor] Awareness state okunurken hata', error);
      }
    };

    awareness.on('update', logAwarenessStates);
    logAwarenessStates();

    return () => {
      if (typeof awareness.off === 'function') {
        awareness.off('update', logAwarenessStates);
      }
    };
  }, [awareness, collaborative]);

  useEffect(() => {
    if (!collaborative || !awareness) {
      return;
    }

    const handleAwarenessStyling = () => {
      syncRemoteSelectionStyles();
    };

    syncRemoteSelectionStyles();

    if (typeof awareness.on === 'function') {
      awareness.on('update', handleAwarenessStyling);
      awareness.on('change', handleAwarenessStyling);
    }

    return () => {
      if (typeof awareness.off === 'function') {
        awareness.off('update', handleAwarenessStyling);
        awareness.off('change', handleAwarenessStyling);
      }
      if (trackedClientRefs.has(awareness)) {
        const trackedClients = trackedClientRefs.get(awareness);
        trackedClientRefs.delete(awareness);
        if (trackedClients && trackedClients.size > 0) {
          let changed = false;
          trackedClients.forEach(clientId => {
            if (remoteClientColorMap.delete(clientId)) {
              changed = true;
            }
          });
          if (changed) {
            updateRemoteSelectionStyleSheet();
          }
        }
      }
    };
  }, [awareness, collaborative, syncRemoteSelectionStyles]);

  function handleEditorDidMount(editor: any, monaco: any) {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Normalize line endings to LF so host/joiner awareness indices stay in sync across OSes
    const model = editor.getModel();
    if (model && model.getEOL() !== '\n' && monaco?.editor?.EndOfLineSequence) {
      model.setEOL(monaco.editor.EndOfLineSequence.LF);
    }

    // IntelliSense setup
    if (enableIntelliSense) {
      if (['javascript', 'typescript', 'javascriptreact', 'typescriptreact'].includes(language)) {
        setupJavaScriptIntelliSense();
      }
      
      registerCustomCompletions();

      // IntelliSense keyboard shortcuts
      editor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.Space,
        () => {
          editor.trigger('keyboard', 'editor.action.triggerSuggest', {});
        }
      );

      if (!collaborative) {
        editor.addCommand(
          monaco.KeyCode.Tab,
          () => {
            const suggestController = editor.getContribution('editor.contrib.suggestController');
            if (suggestController && suggestController.widget && suggestController.widget.value && suggestController.widget.value.state === 2) {
              editor.trigger('keyboard', 'acceptSelectedSuggestion', {});
            } else {
              editor.trigger('keyboard', 'tab', {});
            }
          }
        );
      }
    }

    if (onSave) {
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
        onSave();
      });
    }

    console.log('🎨 [Editor] handleEditorDidMount called');
    console.log('  - Collaborative:', collaborative);
    console.log('  - Y.Doc:', !!ydoc);
    console.log('  - Awareness:', !!awareness);
    setupBinding();
  }

  useEffect(() => {
    console.log('🔄 [Editor] setupBinding useEffect triggered');
    console.log('  - Collaborative:', collaborative);
    console.log('  - Y.Doc:', !!ydoc);
    console.log('  - Awareness:', !!awareness);
    console.log('  - Editor mounted:', !!editorRef.current);
    const cleanup = setupBinding();
    return cleanup;
  }, [setupBinding]);

  useImperativeHandle(ref, () => ({
    getDestroy: () => () => {
      if (bindingRef.current) {
        bindingRef.current.destroy();
        bindingRef.current = null;
      }
    },
    getEditor: () => editorRef.current,
    triggerSuggest: () => {
      if (editorRef.current && enableIntelliSense) {
        editorRef.current.trigger('keyboard', 'editor.action.triggerSuggest', {});
      }
    }
  }), [enableIntelliSense]);

  // Monaco Editor options - tip güvenli şekilde
  const editorOptions: editor.IStandaloneEditorConstructionOptions = {
    // Mevcut ayarlarınız
    minimap: { enabled: true },
    fontSize: 14,
    wordWrap: 'on',
    automaticLayout: true,
    mouseWheelZoom: true,
    cursorBlinking: 'smooth',
    cursorSmoothCaretAnimation: 'on',
    cursorStyle: 'line',
    renderWhitespace: 'selection',
    selectOnLineNumbers: true,
    selectionHighlight: true,
    occurrencesHighlight: 'off',
    renderLineHighlight: 'all',
    renderValidationDecorations: 'on',
    scrollBeyondLastLine: false,
    fixedOverflowWidgets: true,
    lineNumbers: 'on',
    glyphMargin: true,
    folding: true,
    lineDecorationsWidth: 10,
    lineNumbersMinChars: 3,
    contextmenu: true,
    mouseWheelScrollSensitivity: 1,
    scrollbar: {
      vertical: 'visible',
      horizontal: 'visible',
      useShadows: false,
      verticalScrollbarSize: 10,
      horizontalScrollbarSize: 10,
      arrowSize: 11
    },

    // Otomatik kapanış ve formatting
    autoClosingBrackets: 'always',
    autoClosingQuotes: 'always',
    autoSurround: 'languageDefined',
    formatOnPaste: true,
    formatOnType: true,

    // IntelliSense ayarları - koşullu
    quickSuggestions: enableIntelliSense ? {
      other: true,
      comments: true,
      strings: true
    } : false,
    
    quickSuggestionsDelay: enableIntelliSense ? 10 : undefined,
    suggestOnTriggerCharacters: enableIntelliSense,
    acceptSuggestionOnEnter: enableIntelliSense ? 'on' : 'off',
    acceptSuggestionOnCommitCharacter: enableIntelliSense,
    tabCompletion: enableIntelliSense && !collaborative ? 'on' : 'off',
    wordBasedSuggestions: enableIntelliSense ? 'allDocuments' : 'off',
    
    // Parameter hints
    parameterHints: {
      enabled: enableIntelliSense,
      cycle: enableIntelliSense
    },
    
    // Hover bilgileri
    hover: {
      enabled: enableIntelliSense,
      delay: enableIntelliSense ? 300 : undefined,
      sticky: enableIntelliSense
    },
    
    // Suggestion widget ayarları
    suggest: enableIntelliSense ? {
      showMethods: true,
      showFunctions: true,
      showConstructors: true,
      showFields: true,
      showVariables: true,
      showClasses: true,
      showStructs: true,
      showInterfaces: true,
      showModules: true,
      showProperties: true,
      showEvents: true,
      showOperators: true,
      showUnits: true,
      showValues: true,
      showConstants: true,
      showEnums: true,
      showEnumMembers: true,
      showKeywords: true,
      showWords: true,
      showColors: true,
      showFiles: true,
      showReferences: true,
      showFolders: true,
      showTypeParameters: true,
      showSnippets: true,
      filterGraceful: true,
      snippetsPreventQuickSuggestions: false,
      localityBonus: true,
      shareSuggestSelections: true,
      selectionMode: 'always',
      insertMode: 'insert'
    } : {
      showWords: false
    },

    // Suggestion widget görünümü
    suggestFontSize: 14,
    suggestLineHeight: 20
  };

  return (
    <MonacoEditor
      height="100%"
      defaultLanguage={language}
      language={language}
      theme={theme}
      value={collaborative ? undefined : code}
      onChange={collaborative ? undefined : onChange}
      onMount={handleEditorDidMount}
      options={editorOptions}
    />
  );
});

Editor.displayName = 'Editor';

export default Editor;